import { Map } from 'immutable';
import { getCurrencyCode, getCurrencySymbol } from '../../utils/currency';
import { currentPartner } from '../../utils/currentPartner';
import { initialState } from '../reducers/auth';
import { peek, peekOr, selector } from '../selectorHelpers';

// AUTH SELECTORS
export const authSelect = (state) => state.get('auth', initialState);

export const authSelectUser = selector(authSelect, peekOr(['user'], Map()));
export const authSelectAuthenticated = selector(
  authSelect,
  peek('authenticated'),
);
export const authSelectFfid = selector(
  [authSelectAuthenticated, authSelectUser],
  (isAuthenticated, user) =>
    isAuthenticated ? `${currentPartner.ffidPrefix}${user.get('ffid')}` : null,
);
export const authSelectEmail = selector(authSelectUser, peek('email'));
export const authSelectCreatedAt = selector(authSelectUser, peek('created_at'));
export const authSelectFistName = selector(authSelectUser, peek('first_name'));
export const authSelectLastName = selector(authSelectUser, peek('last_name'));
export const authSelectFullName = selector(
  [authSelectAuthenticated, authSelectFistName, authSelectLastName],
  (isAuthenticated, firstName, lastName) =>
    isAuthenticated ? `${firstName} ${lastName}` : null,
);
export const authSelectLocale = selector(authSelect, peek('user', 'locale'));
export const authSelectUserCurrency = selector(
  authSelect,
  peek('user', 'currency'),
);
// Display currency: the account's `currency` from /client_api/user wins;
// the partner config is the pre-login fallback. Unknown codes degrade to
// the code itself (see utils/currency.ts).
export const selectCurrencySymbol = selector(authSelectUserCurrency, (code) =>
  getCurrencySymbol(code, currentPartner.currency.symbol),
);
export const selectCurrencyCode = selector(authSelectUserCurrency, (code) =>
  getCurrencyCode(code, currentPartner.currency.code),
);
export const authSelectUnits = selector(
  authSelect,
  peek('user', 'display_weight_in'),
);
export const authSelectAllowSearchByLocation = selector(
  authSelect,
  peek('user', 'extended_tools'),
);
export const authSelectAllowExtendedTools = selector(
  authSelect,
  peek('user', 'extended_tools'),
);
const authSelectUserType = selector(authSelect, peek('user', 'user_type'));

// Containers are a service of their own — only container clients get the
// section. 'Containers' is the backend's user_type value; anything else,
// including a profile that hasn't loaded yet, stays gated out.
export const authSelectCanUseContainers = selector(
  authSelectUserType,
  (userType) => userType === 'Containers',
);

export const storageIsImpersonateState = selector(
  authSelect,
  peek('impersonated'),
);

const authFeePercentRoot = selector(authSelect, peek('user', 'fee_percent'));

export const authCreditCardFeePercent = selector(
  authFeePercentRoot,
  peek('credit'),
);

export const authPaypalFeePercent = selector(
  authFeePercentRoot,
  peek('paypal'),
);

export const authOverpayFeePercent = selector(
  authFeePercentRoot,
  peek('overpay'),
);

export const authStripeFeePercent = selector(
  authFeePercentRoot,
  peek('stripe'),
);

export const authSelectLoading = selector(authSelect, peek('isLoading'));

// ROUTING SELECTORS
export const selectRouter = (state) => state.get('router');

export const routeSelectPathname = selector(
  selectRouter,
  peek('location', 'pathname'),
);
export const routeSelectState = selector(
  selectRouter,
  peekOr(['location', 'state'], Map()),
);
